/**
 * NayaxStats — Phase 101ea (Feb 2026)
 *
 * Page dédiée /statistiques/nayax pour éliminer les 30 manips mensuelles
 * qu'Alain devait faire dans le portail Nayax.
 *
 * 2 onglets :
 *  1. Ventes mensuelles — tableau croisé Machine × Produit, comparatif M-1
 *  2. Best-Sellers — top produits sur période libre, filtre machine, tri qté/CA
 *
 * Chaque onglet propose un export PDF paysage A4.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  ArrowLeft, BarChart3, Calendar, Crown, Download, Filter, Loader2, RefreshCw, Send, ShoppingCart, TrendingDown, TrendingUp,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const CHF = (v) => (Number(v || 0)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
const fmtInt = (v) => `${Math.round(Number(v || 0)).toLocaleString("fr-CH").replaceAll(",", "'")}`;
const isoDay = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return isoDay(d);
};

function DeltaBadge({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-xs text-[#575A57]">—</span>;
  }
  const positive = value >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const cls = positive
    ? "bg-[#E8F2EC] text-[#1F4E3D] border-[#A8C9B6]"
    : "bg-[#FBEEEA] text-[#A04E2C] border-[#E8B0A0]";
  return (
    <Badge className={`${cls} inline-flex items-center gap-1 text-[10px]`}>
      <Icon className="w-3 h-3" />
      {positive ? "+" : ""}{value.toFixed(1)} %
    </Badge>
  );
}

function Kpi({ label, value, sub, accent }) {
  const color = accent === "success" ? "text-[#1F4E3D]"
    : accent === "danger" ? "text-[#C05C47]"
    : accent === "highlight" ? "text-[#8C6B2F]"
    : "text-[#1A1C1A]";
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-[#6B6E6B]">{label}</div>
        <div className={`text-xl font-semibold font-mono ${color}`}>{value}</div>
        {sub && <div className="text-[10px] text-[#575A57] mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}


// ============ Onglet 1 — Ventes mensuelles ============

function MonthlyReportTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/statistics/nayax/monthly`, {
        params: { year, month },
      });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement rapport");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    try {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      const res = await api.get(`/statistics/nayax/monthly/pdf?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      // libère l'URL après quelques secondes
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error("Erreur export PDF");
    }
  };

  const totals = data?.totals || {};
  const prev = data?.prev_period || {};
  const machines = data?.machines || [];

  return (
    <div className="space-y-4" data-testid="nayax-monthly-tab">
      {/* Contrôles */}
      <Card>
        <CardContent className="p-4 flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs">Année</Label>
            <Input
              type="number"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value || now.getFullYear(), 10))}
              className="w-24"
              data-testid="nayax-monthly-year"
            />
          </div>
          <div>
            <Label className="text-xs">Mois</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
              <SelectTrigger className="w-36" data-testid="nayax-monthly-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS_FR.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="nayax-monthly-refresh">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Rafraîchir
          </Button>
          <Button
            size="sm"
            onClick={downloadPdf}
            disabled={loading || !machines.length}
            className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
            data-testid="nayax-monthly-pdf"
          >
            <Download className="w-4 h-4 mr-1" /> PDF paysage
          </Button>
        </CardContent>
      </Card>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Barquettes vendues" value={fmtInt(totals.nb_barquettes)} />
        <Kpi label="CA brut" value={CHF(totals.ca_brut)} accent="highlight" />
        <Kpi
          label="Δ vs M-1"
          value={
            totals.delta_vs_prev_percent === null || totals.delta_vs_prev_percent === undefined
              ? "—"
              : `${totals.delta_vs_prev_percent >= 0 ? "+" : ""}${totals.delta_vs_prev_percent.toFixed(1)} %`
          }
          sub={`${prev.label || ""} : ${CHF(prev.ca_brut)}`}
          accent={(totals.delta_vs_prev_percent || 0) < 0 ? "danger" : "success"}
        />
        <Kpi label="Machines actives" value={fmtInt(totals.nb_machines)} />
      </div>

      {/* Sections par machine */}
      {loading && (
        <div className="text-sm text-[#575A57] py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin inline mr-2" />
          Chargement…
        </div>
      )}
      {!loading && !machines.length && (
        <div className="bg-[#F0F5FB] border border-[#C3D1E8] rounded p-4 text-sm text-[#2F5A8C]">
          Aucune vente Nayax enregistrée pour {MONTHS_FR[month - 1]} {year}.
        </div>
      )}
      {machines.map((m) => (
        <MachineBlock
          key={m.distributor_id}
          machine={m}
          year={year}
          month={month}
          onSent={load}
        />
      ))}
    </div>
  );
}


function MachineBlock({ machine: m, year, month, onSent }) {
  const [emailOpen, setEmailOpen] = useState(false);

  const downloadMachinePdf = async () => {
    try {
      const params = new URLSearchParams({
        year: String(year),
        month: String(month),
        distributor_id: m.distributor_id,
      });
      const res = await api.get(`/statistics/nayax/monthly/pdf?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error("Erreur export PDF machine");
    }
  };

  return (
    <>
      <Card data-testid={`nayax-monthly-machine-${m.distributor_id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-semibold text-[#1A1C1A]">
                {m.name}
                {m.code && <span className="text-[#575A57] font-normal"> · {m.code}</span>}
                {m.city && <span className="text-[#575A57] font-normal"> · {m.city}</span>}
              </div>
              <div className="text-xs text-[#575A57]">
                {fmtInt(m.nb_barquettes)} barquettes · {CHF(m.ca_brut)} · <DeltaBadge value={m.delta_vs_prev_percent} />
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <div className="text-xs text-[#575A57] text-right">
                Cartes : <span className="font-mono">{CHF(m.ca_cards)}</span>
                <span className="mx-2">·</span>
                TWINT : <span className="font-mono">{CHF(m.ca_twint)}</span>
                <span className="text-[10px] opacity-70"> ({m.nb_tx_twint} tx)</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={downloadMachinePdf}
                className="text-[11px]"
                data-testid={`nayax-machine-pdf-${m.distributor_id}`}
              >
                <Download className="w-3 h-3 mr-1" /> PDF machine
              </Button>
              <Button
                size="sm"
                onClick={() => setEmailOpen(true)}
                className="bg-[#8C6B2F] hover:bg-[#725520] text-white text-[11px]"
                data-testid={`nayax-machine-email-${m.distributor_id}`}
              >
                <Send className="w-3 h-3 mr-1" /> Envoyer au partenaire
              </Button>
            </div>
          </div>

          <div className="border border-[#E5E5E0] rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAF7] border-b border-[#E5E5E0]">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase text-[#575A57]">Produit</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase text-[#575A57]">Barq.</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase text-[#575A57]">CA brut</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase text-[#575A57]">Prix moy.</th>
                </tr>
              </thead>
              <tbody>
                {m.products.map((p) => (
                  <tr key={p.product_id} className="border-t border-[#E5E5E0]">
                    <td className="px-3 py-1.5">
                      {p.name}
                      {p.retired && (
                        <Badge className="ml-2 bg-[#FBEEEA] text-[#A04E2C] border-[#E8B0A0] text-[9px]">
                          ⚠ Retiré du planogramme
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtInt(p.nb_barquettes)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{CHF(p.ca_brut)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-[#575A57]">{CHF(p.avg_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <SendNayaxDialog
        machine={m}
        year={year}
        month={month}
        open={emailOpen}
        onOpenChange={setEmailOpen}
        onSent={onSent}
      />
    </>
  );
}


function SendNayaxDialog({ machine, year, month, open, onOpenChange, onSent }) {
  const [partnerInfo, setPartnerInfo] = useState(null);   // { email, partner_name }
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [customMessage, setCustomMessage] = useState("");

  // Charge l'email du partenaire hébergeur à l'ouverture
  useEffect(() => {
    if (!open || !machine) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/partner-contracts`, {
          params: { distributor_id: machine.distributor_id, include_inactive: false },
        });
        const active = (data || []).find((c) => c.active) || (data || [])[0];
        if (active && active.partner_id) {
          const { data: p } = await api.get(`/partners/${active.partner_id}`);
          setPartnerInfo({ email: p.email || "", partner_name: p.name || "" });
          setToEmail(p.email || "");
        } else {
          setPartnerInfo({ email: "", partner_name: "" });
        }
      } catch {
        setPartnerInfo({ email: "", partner_name: "" });
      } finally {
        setLoading(false);
      }
    })();
  }, [open, machine]);

  const send = async () => {
    if (!toEmail.trim()) {
      toast.error("Email destinataire obligatoire");
      return;
    }
    setSending(true);
    try {
      const params = new URLSearchParams({
        year: String(year),
        month: String(month),
        distributor_id: machine.distributor_id,
      });
      const { data } = await api.post(`/statistics/nayax/monthly/email?${params}`, {
        to_email: toEmail.trim(),
        custom_message: customMessage.trim() || null,
      });
      toast.success(`Envoyé à ${data.to} (${data.partner_name || "partenaire"})`);
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur envoi");
    } finally {
      setSending(false);
    }
  };

  if (!machine) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="nayax-email-dialog">
        <DialogHeader>
          <DialogTitle>Envoyer les stats au partenaire hébergeur</DialogTitle>
          <DialogDescription>
            <b>{machine.name}</b> · {MONTHS_FR[month - 1]} {year}
            <br />
            PDF paysage joint contenant le détail par produit.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Email destinataire *</Label>
            <Input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder={loading ? "Chargement..." : "cafe@partenaire.ch"}
              data-testid="nayax-email-to"
            />
            {partnerInfo && (
              partnerInfo.partner_name ? (
                <div className="text-[10px] text-[#575A57] mt-1">
                  Contrat actif : <b>{partnerInfo.partner_name}</b>
                  {!partnerInfo.email && (
                    <span className="text-[#C05C47] ml-1">— email non renseigné dans /partenaires, saisis-le ici manuellement.</span>
                  )}
                </div>
              ) : (
                <div className="text-[10px] text-[#C05C47] mt-1">
                  Aucun contrat actif trouvé pour cette machine — saisis un email manuellement.
                </div>
              )
            )}
          </div>
          <div>
            <Label className="text-xs">Message personnalisé (optionnel)</Label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={3}
              className="w-full border border-[#E5E5E0] rounded p-2 text-sm"
              placeholder="Merci pour votre accueil, à bientôt !"
              data-testid="nayax-email-message"
            />
          </div>
          <div className="bg-[#F0F5FB] border border-[#C3D1E8] rounded p-2 text-[11px] text-[#2F5A8C]">
            <b>{fmtInt(machine.nb_barquettes)}</b> barquettes vendues ·
            <b className="ml-1">{CHF(machine.ca_brut)}</b> CA brut
            {machine.delta_vs_prev_percent !== null && machine.delta_vs_prev_percent !== undefined && (
              <span> · {machine.delta_vs_prev_percent >= 0 ? "+" : ""}{machine.delta_vs_prev_percent.toFixed(1)} % vs M-1</span>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={send}
            disabled={sending || loading || !toEmail.trim()}
            className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
            data-testid="nayax-email-send"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============ Onglet 2 — Best-Sellers ============

function BestSellersTab() {
  const [dateFrom, setDateFrom] = useState(daysAgo(30));
  const [dateTo, setDateTo] = useState(isoDay(new Date()));
  const [distributorId, setDistributorId] = useState("__all__");
  const [sortBy, setSortBy] = useState("qty");
  const [limit, setLimit] = useState(20);
  // Phase 101gd — Mode discret : masque CA + prix moyen pour partage externe
  // (cuistot du partenaire, collaborateurs sans accès aux chiffres financiers).
  const [discreet, setDiscreet] = useState(false);
  const [distributors, setDistributors] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Charge la liste des distributeurs Nayax pour le filtre
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/distributors");
        setDistributors(
          (data || [])
            .filter((d) => d.type === "AUTOMATIQUE" && !d.archived)
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        );
      } catch {
        // silencieux
      }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, sort_by: sortBy, limit };
      if (distributorId && distributorId !== "__all__") params.distributor_id = distributorId;
      const { data } = await api.get(`/statistics/nayax/best-sellers`, { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement Best-Sellers");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, distributorId, sortBy, limit]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, sort_by: sortBy, limit: String(limit) });
      if (distributorId && distributorId !== "__all__") params.append("distributor_id", distributorId);
      if (discreet) params.append("discreet", "true");
      const res = await api.get(`/statistics/nayax/best-sellers/pdf?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error("Erreur export PDF");
    }
  };

  const items = data?.items || [];
  const totals = data?.totals || {};
  const topByMachine = data?.top_by_machine || [];

  const maxVal = useMemo(() => {
    if (!items.length) return 1;
    return Math.max(
      ...items.map((i) => (sortBy === "qty" ? i.nb_barquettes : i.ca_brut))
    );
  }, [items, sortBy]);

  return (
    <div className="space-y-4" data-testid="nayax-bestsellers-tab">
      {/* Contrôles */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div>
              <Label className="text-xs">Du</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="nayax-bestsellers-from" />
            </div>
            <div>
              <Label className="text-xs">Au</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="nayax-bestsellers-to" />
            </div>
            <div>
              <Label className="text-xs">Machine</Label>
              <Select value={distributorId} onValueChange={setDistributorId}>
                <SelectTrigger data-testid="nayax-bestsellers-machine"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Toutes machines</SelectItem>
                  {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tri</Label>
              <Select value={sortBy} onValueChange={setSortBy} disabled={discreet}>
                <SelectTrigger data-testid="nayax-bestsellers-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="qty">Par quantité</SelectItem>
                  <SelectItem value="revenue" disabled={discreet}>Par chiffre d'affaires</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Top</Label>
              <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v, 10))}>
                <SelectTrigger data-testid="nayax-bestsellers-limit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">Top 10</SelectItem>
                  <SelectItem value="20">Top 20</SelectItem>
                  <SelectItem value="50">Top 50</SelectItem>
                  <SelectItem value="100">Top 100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Button size="sm" variant="outline" onClick={() => { setDateFrom(daysAgo(7)); setDateTo(isoDay(new Date())); }}>7 jours</Button>
            <Button size="sm" variant="outline" onClick={() => { setDateFrom(daysAgo(30)); setDateTo(isoDay(new Date())); }}>30 jours</Button>
            <Button size="sm" variant="outline" onClick={() => { setDateFrom(daysAgo(90)); setDateTo(isoDay(new Date())); }}>90 jours</Button>
            <Button size="sm" variant="outline" onClick={() => { setDateFrom(daysAgo(365)); setDateTo(isoDay(new Date())); }}>1 an</Button>
            {/* Phase 101gd — Toggle Mode discret (masque CA + prix moyen) */}
            <label
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs cursor-pointer select-none transition-colors ${discreet ? "bg-[#8C6B2F] text-white border-[#8C6B2F]" : "bg-white text-[#575A57] border-[#C8C8C0] hover:border-[#8C6B2F]"}`}
              data-testid="nayax-bestsellers-discreet-toggle"
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={discreet}
                onChange={(e) => {
                  const on = e.target.checked;
                  setDiscreet(on);
                  // En mode discret, le tri par CA n'a pas de sens → force qty
                  if (on && sortBy !== "qty") setSortBy("qty");
                }}
              />
              {discreet ? "👁️‍🗨️ Mode discret" : "Mode discret"}
            </label>
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="nayax-bestsellers-refresh">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Rafraîchir
            </Button>
            <Button
              size="sm"
              onClick={downloadPdf}
              disabled={loading || !items.length}
              className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
              data-testid="nayax-bestsellers-pdf"
            >
              <Download className="w-4 h-4 mr-1" /> {discreet ? "PDF discret" : "PDF paysage"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPI */}
      <div className={`grid grid-cols-1 ${discreet ? "md:grid-cols-2" : "md:grid-cols-3"} gap-3`}>
        <Kpi label="Barquettes vendues" value={fmtInt(totals.nb_barquettes)} />
        {!discreet && <Kpi label="CA brut" value={CHF(totals.ca_brut)} accent="highlight" />}
        <Kpi label="Produits distincts" value={fmtInt(totals.distinct_products)} />
      </div>

      {/* Classement avec barres */}
      {loading && (
        <div className="text-sm text-[#575A57] py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin inline mr-2" />
          Chargement…
        </div>
      )}
      {!loading && !items.length && (
        <div className="bg-[#F0F5FB] border border-[#C3D1E8] rounded p-4 text-sm text-[#2F5A8C]">
          Aucune vente Nayax sur cette période.
        </div>
      )}
      {items.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="text-sm font-semibold text-[#1F4E3D] mb-3 flex items-center gap-2">
              <Crown className="w-4 h-4" /> Classement — Top {items.length}
            </div>
            <div className="space-y-1">
              {items.map((it) => {
                const val = sortBy === "qty" ? it.nb_barquettes : it.ca_brut;
                const pct = Math.max(2, Math.round((val / maxVal) * 100));
                return (
                  <div
                    key={it.product_id}
                    className={`grid gap-2 items-center py-1.5 text-sm hover:bg-[#FAFAF7] rounded px-2 ${discreet ? "grid-cols-[40px_1fr_80px]" : "grid-cols-[40px_1fr_80px_100px_80px]"}`}
                    data-testid={`nayax-best-row-${it.rank}`}
                  >
                    <div className="text-[#8C6B2F] font-bold">#{it.rank}</div>
                    <div className="relative">
                      <div
                        className="absolute inset-0 bg-[#E8F2EC] rounded"
                        style={{ width: `${pct}%` }}
                      />
                      <div className="relative pl-2 py-0.5 text-[#1A1C1A] flex items-center gap-2 flex-wrap">
                        {it.name}
                        {it.nayax_recap && (
                          <Badge className="bg-[#EEE9FA] text-[#5847A6] border-[#D4CAF2] text-[9px]" data-testid="bestseller-nayax-badge">
                            Nayax
                          </Badge>
                        )}
                        {it.machines_count > 1 && (
                          <Badge className="bg-[#F0F5FB] text-[#2F5A8C] border-[#C3D1E8] text-[9px]">
                            {it.machines_count} machines
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right font-mono">{fmtInt(it.nb_barquettes)} barq.</div>
                    {!discreet && (
                      <>
                        <div className="text-right font-mono">{CHF(it.ca_brut)}</div>
                        <div className="text-right font-mono text-[#575A57] text-xs">{CHF(it.avg_price)}</div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top 5 par machine (uniquement en vue globale) */}
      {topByMachine.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="text-sm font-semibold text-[#1F4E3D] mb-3 flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" /> Top 5 par machine
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {topByMachine.map((m) => (
                <div key={m.distributor_id} className="border border-[#E5E5E0] rounded p-2">
                  <div className="text-xs font-semibold text-[#1F4E3D] mb-1">{m.distributor_name}</div>
                  <ol className="text-xs space-y-0.5">
                    {m.top.map((t, i) => (
                      <li key={t.product_id} className="flex justify-between gap-2">
                        <span className="truncate">
                          <span className="text-[#8C6B2F] font-semibold">{i + 1}.</span>{" "}
                          {t.name}
                        </span>
                        <span className="font-mono text-[#575A57] shrink-0">
                          {fmtInt(t.nb_barquettes)}{discreet ? " barq." : ` · ${CHF(t.ca_brut)}`}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ============ Page principale ============

export default function NayaxStats() {
  const [tab, setTab] = useState("monthly");

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="nayax-stats-page">
      <div className="flex items-center gap-3">
        <Link to="/statistiques" className="text-[#575A57] hover:text-[#1F4E3D]" data-testid="nayax-stats-back">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="font-heading text-2xl font-medium text-[#1A1C1A] flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#1F4E3D]" />
            Statistiques ventes Nayax
          </h1>
          <p className="text-sm text-[#575A57]">
            Rapport mensuel consolidé (fini les 30 manips !) et Top Best-Sellers sur période libre.
            Toutes les ventes sont lues depuis les tx synchronisées toutes les 15 min.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="nayax-stats-tabs">
          <TabsTrigger value="monthly" data-testid="nayax-stats-tab-monthly">
            <Calendar className="w-4 h-4 mr-1" /> Ventes mensuelles
          </TabsTrigger>
          <TabsTrigger value="bestsellers" data-testid="nayax-stats-tab-best">
            <Crown className="w-4 h-4 mr-1" /> Best-Sellers
          </TabsTrigger>
        </TabsList>
        <TabsContent value="monthly" className="mt-4">
          <MonthlyReportTab />
        </TabsContent>
        <TabsContent value="bestsellers" className="mt-4">
          <BestSellersTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
